import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Search, Plus, ExternalLink, Trash2, LogOut, Sparkles, Check, Store } from "lucide-react";

const faviconOf = (url) => {
    try {
        const u = new URL(url);
        return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
    } catch { return ""; }
};
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; } };

export default function Brands() {
    const { logout } = useAuth();
    const nav = useNavigate();
    const [brands, setBrands] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [candidates, setCandidates] = useState(null); // {name, candidates:[{url,label,source}]}
    const [resolving, setResolving] = useState(false);
    const inputRef = useRef();

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data } = await api.get("/brands", { params: query ? { q: query } : {} });
            setBrands(data);
        } catch (e) {
            toast.error("Failed to load brands");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // Debounce the search as the user types
    useEffect(() => {
        const id = setTimeout(() => load(q), 200);
        return () => clearTimeout(id);
    }, [q]);

    const shown = useMemo(() => brands, [brands]);

    const resolve = async (name) => {
        const n = (name ?? q).trim();
        if (!n) { toast.error("Type a brand name first"); return; }
        setResolving(true);
        try {
            const { data } = await api.post("/brands/resolve", { name: n });
            setCandidates(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't resolve");
        } finally { setResolving(false); }
    };

    const addBrand = async (name, url) => {
        try {
            await api.post("/brands", { name, url });
            toast.success(`${name} added`);
            setCandidates(null);
            setQ("");
            load();
            inputRef.current?.focus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        }
    };

    const del = async (b) => {
        if (!window.confirm(`Remove ${b.name} from your directory?`)) return;
        await api.delete(`/brands/${b.id}`);
        setBrands((rows) => rows.filter((x) => x.id !== b.id));
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => nav("/wishlist")} className="border border-neutral-300 h-10 w-10 flex items-center justify-center hover:border-black" data-testid="back-to-wishlist">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Shop directory</div>
                            <h1 className="font-display text-2xl tracking-tight leading-none mt-0.5">Brands</h1>
                        </div>
                    </div>
                    <button onClick={logout} className="border border-neutral-300 h-10 w-10 flex items-center justify-center hover:border-black" title="Sign out">
                        <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-6">
                {/* Search + add */}
                <div className="bg-white border border-neutral-200 p-4 mb-6">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Find or add a brand</div>
                    <div className="flex gap-2">
                        <div className="flex items-center border border-neutral-300 flex-1 h-11 px-3 focus-within:border-black">
                            <Search className="h-4 w-4 text-neutral-400 shrink-0 mr-2" strokeWidth={1.5} />
                            <input
                                ref={inputRef}
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (shown.length === 0) resolve(); }}}
                                placeholder="Type a brand — e.g. True Religion, Nike, Neiman Marcus…"
                                className="w-full h-full outline-none font-body text-sm bg-transparent"
                                data-testid="brand-search-input"
                                autoFocus
                            />
                        </div>
                        <button
                            onClick={() => resolve()}
                            disabled={resolving || !q.trim()}
                            className="bg-black text-white h-11 px-4 uppercase tracking-widest text-[10px] font-semibold hover:bg-neutral-800 disabled:bg-neutral-300 flex items-center gap-2"
                            data-testid="brand-find-btn"
                        >
                            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                            {resolving ? "Finding" : "Find site"}
                        </button>
                    </div>
                    <p className="text-[10px] text-neutral-400 font-body mt-2">
                        Search matches your saved brands as you type. Hit <span className="uppercase tracking-widest">Find site</span> for anything new — we'll suggest the official URL.
                    </p>
                </div>

                {/* Candidate picker */}
                {candidates && (
                    <div className="bg-white border-2 border-yellow-300 p-4 mb-6" data-testid="brand-candidates">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Suggested links for</div>
                                <div className="font-display text-2xl leading-none mt-0.5">{candidates.name}</div>
                            </div>
                            <button onClick={() => setCandidates(null)} className="text-neutral-500 hover:text-black text-xs uppercase tracking-widest">Close</button>
                        </div>
                        <div className="space-y-2">
                            {candidates.candidates.length === 0 && (
                                <div className="text-sm text-neutral-500 font-body">No suggestions — try a different spelling.</div>
                            )}
                            {candidates.candidates.map((c) => (
                                <div key={c.url} className="border border-neutral-200 p-2 flex items-center gap-3" data-testid={`candidate-${c.source}`}>
                                    <img src={faviconOf(c.url)} alt="" className="w-8 h-8 bg-neutral-50 border border-neutral-200 shrink-0" onError={(e) => e.target.style.visibility = "hidden"} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-body truncate">{c.label}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                                            {c.source === "curated" ? "✓ known brand" : "guessed domain — verify first"}
                                        </div>
                                    </div>
                                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="border border-neutral-300 h-9 px-3 flex items-center gap-1 text-xs uppercase tracking-widest hover:border-black">
                                        <ExternalLink className="h-3 w-3" strokeWidth={1.5} /> Open
                                    </a>
                                    <button onClick={() => addBrand(candidates.name, c.url)} className="bg-black text-white h-9 px-3 flex items-center gap-1 text-xs uppercase tracking-widest hover:bg-neutral-800" data-testid={`save-${c.source}`}>
                                        <Plus className="h-3 w-3" strokeWidth={1.5} /> Save
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Directory grid */}
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
                    {loading ? "Loading…" : `${shown.length} brand${shown.length === 1 ? "" : "s"}`}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="brand-grid">
                    {shown.map((b) => (
                        <div key={b.id} className="bg-white border border-neutral-200 p-3 flex flex-col group" data-testid={`brand-${b.id}`}>
                            <a href={b.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 flex-1">
                                <div className="w-10 h-10 bg-neutral-50 border border-neutral-200 flex items-center justify-center shrink-0 overflow-hidden">
                                    {b.url ? (
                                        <img src={faviconOf(b.url)} alt="" className="w-6 h-6" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                                    ) : null}
                                    <div className="w-6 h-6 hidden items-center justify-center text-neutral-400"><Store className="h-4 w-4" strokeWidth={1.5} /></div>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-display text-base leading-tight line-clamp-2">{b.name}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate mt-0.5">{hostOf(b.url)}</div>
                                    {b.seeded && <div className="text-[9px] uppercase tracking-widest text-green-700 mt-1 flex items-center gap-1"><Check className="h-3 w-3" strokeWidth={2} /> verified</div>}
                                </div>
                            </a>
                            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-neutral-100">
                                <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest border border-black px-2 h-8 flex-1 flex items-center justify-center gap-1 hover:bg-black hover:text-white" data-testid={`visit-${b.id}`}>
                                    <ExternalLink className="h-3 w-3" strokeWidth={1.5} /> Visit
                                </a>
                                <button onClick={() => del(b)} className="border border-neutral-300 h-8 w-8 flex items-center justify-center hover:border-red-700 hover:text-red-700" title="Remove" data-testid={`del-${b.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {!loading && shown.length === 0 && q.trim() && !candidates && (
                    <div className="border border-dashed border-neutral-300 p-8 text-center bg-white mt-4">
                        <div className="font-display text-xl">No match for "{q}"</div>
                        <div className="text-sm text-neutral-500 font-body mt-2">Hit <span className="uppercase tracking-widest">Find site</span> and I'll suggest the official URL.</div>
                    </div>
                )}
            </main>
        </div>
    );
}
